import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { environment } from '../../environments/environment';
import {
  AmipassInitInput,
  APINoDataResponse,
  UserPaymentMethods,
  LoginUrlResponseData,
  User,
  UpdateUserResponseData,
  UserPersonalData,
  APIDataResponse,
  TwilioVerifyResponseData,
  FAQ,
  Store,
  Coupons,
  LegalDocument,
  LegalDocumentType,
  CatalogData,
  TransactionRating,
  ContactFormData,
  UserIssue,
  UserIssueResponse,
  Coupon,
  TransactionResults,
  TransactionPayment,
  TransactionSummary,
  ProductsGroupsDetailsResponse,
  Cardholder,
  TransbankCardInscriptionBody,
  KlapCardInscriptionBody,
  KlapPayDebtBody,
  KlapPayDebtData,
  LastStoreEntry,
  AccessCredentialsMetadata,
  GoogleWalletSaveLink,
  ValidationPreview,
  OpenData,
  EdenredAccount,
  EdenredLinkInitResult,
  EdenredRechargeInput,
  EdenredRechargeResult,
  EdenredTenantSlug,
  LegalDocumentRequirement,
  ConsentState,
  ConsentEvent,
  ConsentChannel,
  ConsentPurpose,
  DocumentAcceptance,
} from '../interfaces';
import { firstValueFrom } from 'rxjs';
import { SessionStorageService } from './session-storage.service';
import { TokenService } from './token.service';

/** Session-storage key holding a cached legal document body, per type and language. */
export const legalDocumentCacheKey = (type: LegalDocumentType, lang: string) =>
  `legal-document-${type}-${lang}`;

@Injectable({
  providedIn: 'root',
})
export class HttpService {
  private apiUrl = environment.apiUrl;
  clientId: string = environment.clientId;

  constructor(
    private http: HttpClient,
    private token: TokenService,
    private sessionStorageService: SessionStorageService ) { }

  private async accessToken(forceRefresh: boolean = false): Promise<string|null> {
    return await this.token.getToken(forceRefresh);
  }

  /**
   * Standard authenticated JSON headers. Device headers are added by `UserDeviceInterceptor`.
   *
   * `Access-Control-Allow-Origin` is deliberately absent: it is a *response* header, so sending it
   * on a request has never done anything except add a non-simple header that forces a CORS
   * preflight. The call sites this helper replaced all carried it; dropping it here removes it from
   * all of them at once.
   */
  private authHeaders(accessToken: string | null): { [key: string]: string } {
    const headersObj: { [key: string]: string } = {
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    };
    if (accessToken) {
      headersObj['Authorization'] = `Bearer ${accessToken}`;
    }
    return headersObj;
  }

  /**
   * Fetch (and persist) this account's server-signed device id.
   *
   * The server re-serves the id the account is already known by rather than minting a new one, which
   * is what stops a cleared localStorage from detaching the account from its device history.
   */
  async ensureUserDeviceId(): Promise<string | null> {
    const accessToken = await this.accessToken();
    if (!accessToken) {
      return null;
    }
    const url = `${this.apiUrl}/users/device/id`;
    const httpOptions = { headers: new HttpHeaders(this.authHeaders(accessToken)) };
    try {
      const response = await firstValueFrom(
        this.http.post<APIDataResponse<{ deviceId: string }>>(url, {}, httpOptions)
      );
      return response.successful ? response.data.deviceId : null;
    } catch {
      return null;
    }
  }

  private emulateResponse<T>(successful: boolean, data: T, message: string): APIDataResponse<T> {
    return {
      successful: successful,
      data: data,
      message: message
    }
  }

  private async getFromApi<T>(
    url: string,
    searchParams?: { [key: string]: any },
    requiresToken: boolean = true,
    sessionStorageKey?: string,
    sessionStorageExpirationInSeconds?: number
  ): Promise<APIDataResponse<T>> {
    const sessionStorageData: T | null = sessionStorageKey ? this.sessionStorageService.getItem(sessionStorageKey) : null;
    if (sessionStorageData !== null) {
      return this.emulateResponse<T>(true, sessionStorageData, 'Data found in session storage');
    }
    const headersObject = {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*'
    };
    if (requiresToken) {
      let accessToken = await this.accessToken();
      if (!accessToken) {
        return this.emulateResponse(false, null, 'No auth token');
      }
      headersObject['Authorization'] = `Bearer ${accessToken}`;
    }
    const httpOptions = {
      headers: new HttpHeaders(headersObject),
    }
    const urlObject = new URL(url);
    const paramsObject = new URLSearchParams(searchParams ?? {});
    urlObject.search = paramsObject.toString();
    const response: APIDataResponse<T> = await firstValueFrom(this.http.get<APIDataResponse<T>>(urlObject.toString(), httpOptions));
    if (sessionStorageKey && response.successful) {
      this.sessionStorageService.setItem(sessionStorageKey, response.data, sessionStorageExpirationInSeconds);
    }
    return response;
    }

  /**
   * POST counterpart of `getFromApi`: resolves the auth token (when required)
   * and sends the standard API headers, so callers don't hand-roll them.
   */
  private async postToApi<T>(
    url: string,
    body: any = {},
    requiresToken: boolean = true
  ): Promise<APIDataResponse<T>> {
    const headersObject: { [key: string]: string } = {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*'
    };
    if (requiresToken) {
      const accessToken = await this.accessToken();
      if (!accessToken) {
        return this.emulateResponse(false, null, 'No auth token');
      }
      headersObject['Authorization'] = `Bearer ${accessToken}`;
    }
    const httpOptions = {
      headers: new HttpHeaders(headersObject),
    };
    return firstValueFrom(this.http.post<APIDataResponse<T>>(url, body, httpOptions));
  }

  /**
   * PUT counterpart of `postToApi`, for endpoints that replace a value rather than append one.
   */
  private async putToApi<T>(
    url: string,
    body: any = {},
    requiresToken: boolean = true
  ): Promise<APIDataResponse<T>> {
    const headersObject: { [key: string]: string } = {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*'
    };
    if (requiresToken) {
      const accessToken = await this.accessToken();
      if (!accessToken) {
        return this.emulateResponse(false, null, 'No auth token');
      }
      headersObject['Authorization'] = `Bearer ${accessToken}`;
    }
    const httpOptions = {
      headers: new HttpHeaders(headersObject),
    };
    return firstValueFrom(this.http.put<APIDataResponse<T>>(url, body, httpOptions));
  }

  async getUserPersonalData(): Promise<APIDataResponse<UserPersonalData>> {
    const url = `${this.apiUrl}/users/personal-data`;
    return this.getFromApi<UserPersonalData>(url);
  }

  async getUserRunChecked(): Promise<APIDataResponse<boolean>> {
    const url = `${this.apiUrl}/users/run-checked`;
    return this.getFromApi<boolean>(url);
  }

  async getUserPaymentMethods(): Promise<APIDataResponse<UserPaymentMethods>> {
    const url = `${this.apiUrl}/users/payment-methods`;
    const searchParams = { normalizeCards: 'true' };
    return this.getFromApi<UserPaymentMethods>(url, searchParams);
  }

  async sendTwilioRequest(to: string, captcha: string, channel: string = 'whatsapp'): Promise<APIDataResponse<{ sid: string }>> {
    const urlBase = `${this.apiUrl}/twilio/request`;
    const url = new URL(urlBase);
    const httpOptions = {
      headers: new HttpHeaders({
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'X-Recaptcha-Token': captcha
      }),
    };
    let data = {
      to: to,
      channel: channel
    }
    return firstValueFrom(this.http.post<APIDataResponse<{ sid: string }>>(url.toString(), data, httpOptions));
  }

  async sendTwilioPhoneVerify(to: string, otp: string, create: boolean = false, update: boolean = false): Promise<APIDataResponse<TwilioVerifyResponseData>> {
    const urlBase = `${this.apiUrl}/twilio/phone/verify`;
    const url = new URL(urlBase);
    const headersObj: { [key: string]: string } = {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    };
    if (update) {
      const token = await this.accessToken(true);
      if (token) headersObj['Authorization'] = `Bearer ${token}`;
    }
    const httpOptions = { headers: new HttpHeaders(headersObj) };
    const data = { to, code: otp, create, update };
    return firstValueFrom(this.http.post<APIDataResponse<TwilioVerifyResponseData>>(url.toString(), data, httpOptions));
  }

  async sendTwilioEmailVerify(to: string, otp: string, create: boolean = false, update: boolean = false): Promise<APIDataResponse<TwilioVerifyResponseData>> {
    const urlBase = `${this.apiUrl}/twilio/email/verify`;
    const url = new URL(urlBase);
    const headersObj: { [key: string]: string } = {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    };
    if (update) {
      const token = await this.accessToken(true);
      if (token) headersObj['Authorization'] = `Bearer ${token}`;
    }
    const httpOptions = { headers: new HttpHeaders(headersObj) };
    const data = { to, code: otp, create, update };
    return firstValueFrom(this.http.post<APIDataResponse<TwilioVerifyResponseData>>(url.toString(), data, httpOptions));
  }

  // TODO: This POST should have data.
  async rateTransaction(transactionId: string, rating: number, comments: string = '', superTransactionId: string | null = null, requiresToken: boolean = true): Promise<APINoDataResponse> {
    let accessToken: string | null = null;
    if (requiresToken) {
      accessToken = await this.accessToken();
      if (!accessToken) {
        let response: APINoDataResponse = this.emulateResponse(false, null, 'No auth token');
        return response;
      }
    }
    const urlBase = `${this.apiUrl}/transactions/${transactionId}/rate`;
    const url = new URL(urlBase);
    const headersObj: { [key: string]: string } = {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    };
    if (accessToken) { headersObj['Authorization'] = `Bearer ${accessToken}`; }
    const httpOptions = {
      headers: new HttpHeaders(headersObj),
    };
    url.searchParams.append('rating', rating.toString());
    if (comments) {
      url.searchParams.append('comments', comments);
    }
    if (superTransactionId) {
      url.searchParams.append('superTransactionId', superTransactionId);
    }
    return firstValueFrom(this.http.post<APINoDataResponse>(url.toString(), {}, httpOptions));
  }

  async getUserPaymentMethodsForStore(
    storeId: string,
    allowNonFloatBalances: boolean
  ): Promise<APIDataResponse<UserPaymentMethods>> {
    const urlBase = `${this.apiUrl}/users/payment-methods-for-store/${storeId}`;
    const searchParams: { [key: string]: string } = { normalizeCards: 'true' };
    searchParams['allowNonFloatBalances'] = allowNonFloatBalances ? 'true' : 'false';
    return this.getFromApi<UserPaymentMethods>(urlBase, searchParams);
  }

    async getUserPendingCount(): Promise<APIDataResponse<number>> {
    const url = `${this.apiUrl}/users/pending/count`;
    return this.getFromApi<number>(url);
  }

  async getLastStores(): Promise<APIDataResponse<LastStoreEntry[]>> {
    const url = `${this.apiUrl}/users/last-stores`;
    const searchParams: { [key: string]: string } = {};
    if (this.clientId) {
      searchParams['clientId'] = this.clientId;
    }
    return this.getFromApi<LastStoreEntry[]>(url, searchParams);
  }

    async getUserPendingSum(): Promise<APIDataResponse<number>> {
    const url = `${this.apiUrl}/users/pending/sum`;
    return this.getFromApi<number>(url);
  }

  async getUserRejectedCount(): Promise<APIDataResponse<number>> {
    const url = `${this.apiUrl}/users/rejected/count`;
    return this.getFromApi<number>(url);
  }

    async getUserRejectedSum(): Promise<APIDataResponse<number>> {
    const url = `${this.apiUrl}/users/rejected/sum`;
    return this.getFromApi<number>(url);
  }

  async getTransactionHistory(startAfter: number | null = null): Promise<APIDataResponse<TransactionSummary[]>> {
    const url = `${this.apiUrl}/transactions/history`;
    const paramsObject = {};
    if (startAfter) {
      paramsObject['startAfter'] = startAfter.toString();
    }
    return this.getFromApi<TransactionSummary[]>(url, paramsObject);
  }

  async getTransactionSummaryById(transactionId: string, requiresToken: boolean = true): Promise<APIDataResponse<TransactionSummary>> {
    const url = `${this.apiUrl}/transactions/${transactionId}/history`;
    return this.getFromApi<TransactionSummary>(url, {}, requiresToken);
  }

  async bindTransaction(transactionId: string): Promise<APINoDataResponse> {
    const accessToken = await this.accessToken();
    if (!accessToken) {
      return this.emulateResponse(false, null, 'No auth token');
    }
    const url = `${this.apiUrl}/transactions/${transactionId}/bind`;
    const httpOptions = {
      headers: new HttpHeaders({
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`
      }),
    };
    return firstValueFrom(this.http.post<APINoDataResponse>(url, {}, httpOptions));
  }

  async getUserTransactionsCount(): Promise<APIDataResponse<number>> {
    const url = `${this.apiUrl}/transactions/me/count`;
    return this.getFromApi<number>(url);
  }

  async getTransactionRating(transactionId: string, requiresToken: boolean = true): Promise<APIDataResponse<TransactionRating>> {
    const url = `${this.apiUrl}/transactions/${transactionId}/rating`;
    return this.getFromApi<TransactionRating>(url, null, requiresToken, `rating-${transactionId}`, 3600);
  }

  async getTransactionRated(transactionId: string, requiresToken: boolean = true): Promise<APIDataResponse<boolean>> {
    const url = `${this.apiUrl}/transactions/${transactionId}/rated`;
    return this.getFromApi<boolean>(url, {}, requiresToken);
  }

  async getTransactionResults(transactionId: string, requiresToken: boolean = true): Promise<APIDataResponse<TransactionResults>> {
    const url = `${this.apiUrl}/transactions/${transactionId}/results`;
    return this.getFromApi<TransactionResults>(url, {}, requiresToken);
  }

  async getTransactionPayments(transactionId: string, requiresToken: boolean = true): Promise<APIDataResponse<TransactionPayment[]>> {
    const url = `${this.apiUrl}/transactions/${transactionId}/payments`;
    return this.getFromApi<TransactionPayment[]>(url, {}, requiresToken);
  }

  async getStoreCatalog(storeId: string): Promise<APIDataResponse<CatalogData>> {
    const sessionStorageKey: string = `storeCatalog-${storeId}`
    const url = `${this.apiUrl}/stores/${storeId}/catalog`;
    return this.getFromApi<CatalogData>(url, null, false, sessionStorageKey);
  }

  async getStore(storeId: string): Promise<APIDataResponse<Store>> {
    const sessionStorageKey: string = `store-${storeId}`
    const url = `${this.apiUrl}/stores/${storeId}`;
    return this.getFromApi<Store>(url, null, false, sessionStorageKey);
  }

  async getStoreValidationPreview(storeId: string): Promise<APIDataResponse<ValidationPreview>> {
    // Requires the user's token (the preview depends on their cards/transaction history) and is
    // intentionally NOT session-cached, since it varies per user and card state.
    const url = `${this.apiUrl}/stores/${storeId}/validation-preview`;
    return this.getFromApi<ValidationPreview>(url);
  }

  async getStoreIdFromShortId(shortId: string): Promise<APIDataResponse<string>> {
    const url = `${this.apiUrl}/stores/short/${shortId}`;
    return this.getFromApi<string>(url, null, false);
  }

  async getStoreName(storeId: string): Promise<APIDataResponse<string>> {
    const url = `${this.apiUrl}/stores/${storeId}/name`;
    const sessionStorageKey: string = `storeName-${storeId}`;
    return this.getFromApi<string>(url, null, false, sessionStorageKey);
  }

  /**
   * Open a store's cooler.
   *
   */
  async openStore(storeId: string, securityCode: string | null, deviceId: string | null): Promise<APIDataResponse<OpenData>> {
    let accessToken = await this.accessToken();
    if (!accessToken) {
      let response: APIDataResponse<OpenData> = this.emulateResponse(false, null, 'No auth token');
      return response;
    }
    const url = `${this.apiUrl}/stores/${storeId}/open`;
    const httpOptions = { headers: new HttpHeaders(this.authHeaders(accessToken)) };
    let data: { [key: string]: any } = {};
    if (securityCode) {
      data["encodedSecurityCode"] = btoa(securityCode);
    }
    if (deviceId) {
      data["deviceId"] = deviceId;
    }
    // Was declared `{transactionId: string}`, but callers read `data.superTransactionId` too; it only
    // compiled because the consumer re-annotated the `.then` parameter.
    return firstValueFrom(this.http.post<APIDataResponse<OpenData>>(url, data, httpOptions));
  }

  async transbankStartCardInscription(storeId: string | null): Promise<APIDataResponse<string>> {
    let accessToken = await this.accessToken();
    let data: { [key: string]: any } = {
      base_url: `${location.origin}/card-inscription`,
    };
    if (storeId) {
      // Legacy parameter name
      data["coolerId"] = storeId;
    }
    const url = `${this.apiUrl}/users/transbank/card`;
    const httpOptions = { headers: new HttpHeaders(this.authHeaders(accessToken)) };
    return firstValueFrom(this.http.post<APIDataResponse<string>>(url, data, httpOptions));
  }

  async klapStartCardInscription(cardholder?: Cardholder, coolerId: string | null = null): Promise<APIDataResponse<string>> {
    let accessToken = await this.accessToken();
    const url = `${this.apiUrl}/users/klap/card`;
    const httpOptions = { headers: new HttpHeaders(this.authHeaders(accessToken)) };
    const body: KlapCardInscriptionBody = { clientId: this.clientId };
    if (cardholder) {
      body.cardholder = cardholder;
    }
    if (coolerId) {
      // Legacy parameter name for the store id. Sent so the backend can resolve
      // per-store/client prepaid and debit inscription policies at confirm time.
      body.coolerId = coolerId;
    }
    return firstValueFrom(this.http.post<APIDataResponse<string>>(url, body, httpOptions));
  }

  /**
   * Returns the caller's active store-access credential metadata, if any.
   * The API responds 404 when the user has no active credential; callers
   * should treat that as "no pass yet".
   */
  async getMyAccessCredentials(): Promise<APIDataResponse<AccessCredentialsMetadata>> {
    const url = `${this.apiUrl}/access-credentials/me`;
    return this.getFromApi<AccessCredentialsMetadata>(url);
  }

  /**
   * Mints (if needed) a store-access credential and returns a Google Wallet
   * "Save to Wallet" link. The API responds 409 when an active credential
   * already exists, because its plaintext secret is no longer available to
   * embed in a new pass; in that case the caller must revoke and retry.
   */
  async addToGoogleWallet(): Promise<APIDataResponse<GoogleWalletSaveLink>> {
    const url = `${this.apiUrl}/access-credentials/google-wallet`;
    return this.postToApi<GoogleWalletSaveLink>(url);
  }

  /**
   * Revokes the caller's active store-access credential. After revoking, a
   * fresh credential (and a new wallet pass) can be issued.
   */
  async revokeAccessCredentials(): Promise<APIDataResponse<AccessCredentialsMetadata>> {
    const url = `${this.apiUrl}/access-credentials/revoke`;
    return this.postToApi<AccessCredentialsMetadata>(url);
  }

  async klapPayDebt(
    transactionId: string,
    requiresToken: boolean = true,
    body: KlapPayDebtBody = {},
  ): Promise<APIDataResponse<KlapPayDebtData>> {
    let accessToken: string | null = null;
    if (requiresToken) {
      accessToken = await this.accessToken();
      if (!accessToken) {
        return this.emulateResponse(false, null, 'No auth token');
      }
    }
    const url = `${this.apiUrl}/transactions/${transactionId}/klap/pay-debt`;
    const headersObj: { [key: string]: string } = {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
    };
    if (accessToken) { headersObj['Authorization'] = `Bearer ${accessToken}`; }
    const httpOptions = {
      headers: new HttpHeaders(headersObj),
    };
    return firstValueFrom(this.http.post<APIDataResponse<KlapPayDebtData>>(url, body, httpOptions));
  }

  async deleteKlapCard(cardIndex: number = 0): Promise<APINoDataResponse> {
    let accessToken = await this.accessToken();
    const url = `${this.apiUrl}/users/klap/card?clientId=${this.clientId}&cardIndex=${cardIndex}`;
    const httpOptions = {
      headers: new HttpHeaders({
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Access-Control-Allow-Origin': '*',
        'Authorization': `Bearer ${accessToken}`
      }),
    };
    return firstValueFrom(this.http.delete<APINoDataResponse>(url, httpOptions));
  }

  async amipassInit(initData: AmipassInitInput): Promise<APIDataResponse<string>> {
    let accessToken = await this.accessToken();
    const url = `${this.apiUrl}/recharges/amipass/init`;
    const httpOptions = {
      headers: new HttpHeaders({
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Access-Control-Allow-Origin': '*',
        'Authorization': `Bearer ${accessToken}`
      }),
    };
    return await firstValueFrom(this.http.post<APIDataResponse<string>>(url, initData, httpOptions));
  }

  async amipassValidate(idPedido: string): Promise<APINoDataResponse> {
    let accessToken = await this.accessToken();
    const url = `${this.apiUrl}/recharges/amipass/validate/${idPedido}`;
    const httpOptions = {
      headers: new HttpHeaders({
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Access-Control-Allow-Origin': '*',
        'Authorization': `Bearer ${accessToken}`
      }),
    };
    let response: APINoDataResponse = await firstValueFrom(this.http.patch<APINoDataResponse>(url, {}, httpOptions));
    return response;
  }

  async amipassDecline(idFracaso: string): Promise<APINoDataResponse> {
    let accessToken = await this.accessToken();
    const url = `${this.apiUrl}/recharges/amipass/decline/${idFracaso}`;
    const httpOptions = {
      headers: new HttpHeaders({
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Access-Control-Allow-Origin': '*',
        'Authorization': `Bearer ${accessToken}`
      }),
    };
    let response: APINoDataResponse = await firstValueFrom(this.http.patch<APINoDataResponse>(url, {}, httpOptions));
    return response;
  }

  /**
   * Lists the user's Edenred products and whether each one is linked.
   * A linked product can be recharged without any browser redirect.
   */
  async edenredAccounts(): Promise<APIDataResponse<EdenredAccount[]>> {
    let accessToken = await this.accessToken();
    const url = `${this.apiUrl}/wallets/edenred`;
    const httpOptions = {
      headers: new HttpHeaders({
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Access-Control-Allow-Origin': '*',
        'Authorization': `Bearer ${accessToken}`
      }),
    };
    return await firstValueFrom(this.http.get<APIDataResponse<EdenredAccount[]>>(url, httpOptions));
  }

  /** Starts the Edenred login and returns the URL the browser must be sent to. */
  async edenredLinkInit(slug: EdenredTenantSlug): Promise<APIDataResponse<EdenredLinkInitResult>> {
    let accessToken = await this.accessToken();
    const url = `${this.apiUrl}/wallets/edenred/${slug}/link`;
    const httpOptions = {
      headers: new HttpHeaders({
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Access-Control-Allow-Origin': '*',
        'Authorization': `Bearer ${accessToken}`
      }),
    };
    return await firstValueFrom(this.http.post<APIDataResponse<EdenredLinkInitResult>>(url, {}, httpOptions));
  }

  /** Unlinks an Edenred product. Existing internal balance is untouched. */
  async edenredUnlink(slug: EdenredTenantSlug): Promise<APINoDataResponse> {
    let accessToken = await this.accessToken();
    const url = `${this.apiUrl}/wallets/edenred/${slug}`;
    const httpOptions = {
      headers: new HttpHeaders({
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Access-Control-Allow-Origin': '*',
        'Authorization': `Bearer ${accessToken}`
      }),
    };
    return await firstValueFrom(this.http.delete<APINoDataResponse>(url, httpOptions));
  }

  /**
   * Charges the linked Edenred account and credits the matching internal balance.
   * Responds 409 when no account is linked yet, which is the cue to run edenredLinkInit.
   */
  async edenredRecharge(slug: EdenredTenantSlug, rechargeData: EdenredRechargeInput): Promise<APIDataResponse<EdenredRechargeResult>> {
    let accessToken = await this.accessToken();
    const url = `${this.apiUrl}/recharges/edenred/${slug}`;
    const httpOptions = {
      headers: new HttpHeaders({
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Access-Control-Allow-Origin': '*',
        'Authorization': `Bearer ${accessToken}`
      }),
    };
    return await firstValueFrom(this.http.post<APIDataResponse<EdenredRechargeResult>>(url, rechargeData, httpOptions));
  }

  async confirmInscription(tbk_token: string, username: string, coolerId: string | null = null): Promise<APIDataResponse<{tbk_response_code: number}>> {
    let accessToken = await this.accessToken();
    let data: TransbankCardInscriptionBody = {
      tbk_token: tbk_token,
      username: username,
      clientId: this.clientId,
    }
    if (coolerId) {
      // Legacy parameter name for the store id. Sent so the backend can resolve
      // per-store/client prepaid and debit inscription policies at confirm time.
      data.coolerId = coolerId;
    }
    const url = `${this.apiUrl}/users/transbank/card`;
    const httpOptions = {
      headers: new HttpHeaders({
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Access-Control-Allow-Origin': '*',
        'Authorization': `Bearer ${accessToken}`
      }),
    };
    return firstValueFrom(this.http.patch<APIDataResponse<{tbk_response_code: number}>>(url, data, httpOptions));
  }

  async deleteTransbankCard(cardIndex: number = 0): Promise<APIDataResponse<{tbk_response_code: number}>> {
    let accessToken = await this.accessToken();
    const url = `${this.apiUrl}/users/transbank/card?cardIndex=${cardIndex}`;
    const httpOptions = {
      headers: new HttpHeaders({
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Access-Control-Allow-Origin': '*',
        'Authorization': `Bearer ${accessToken}`
      }),
    };
    return firstValueFrom(this.http.delete<APIDataResponse<{tbk_response_code: number}>>(url, httpOptions));
  }

  /**
   * `consents` is required in practice: the API rejects account creation from a consent-capable
   * client that omits the legal documents. It is typed optional to keep it a trailing argument,
   * not because omitting it is a supported call.
   */
  async createUser(coolerId: string, email: string, name?: string, areaCode: string | null = null, phoneNumber: string | null = null, consents: DocumentAcceptance[] | null = null): Promise<APIDataResponse<User>> {
    let accessToken = await this.accessToken(true);
    const url = `${this.apiUrl}/users/create`;
    const httpOptions = { headers: new HttpHeaders(this.authHeaders(accessToken)) };
    const data = {
      coolerId: coolerId,
      email: email,
      name: name,
      areaCode: areaCode,
      phoneNumber: phoneNumber,
      consents: consents
    };
    return firstValueFrom(this.http.post<APIDataResponse<User>>(url, data, httpOptions));
  }

  /**
   * `consents` is only meaningful on the call that *creates* the profile — the registration flow's
   * first PATCH. The API records it there and rejects the call when a consent-capable client omits
   * the required documents; on later profile edits it is simply absent.
   */
  async updateUser(id: string, name: string | null = null, email: string | null = null, areaCode: string | null = null, phoneNumber: string | null = null, coolerId: string | null = null, consents: DocumentAcceptance[] | null = null): Promise<APIDataResponse<User>> {
    let accessToken = await this.accessToken(true);
    const url = `${this.apiUrl}/users/${id}`;
    const httpOptions = {
      headers: new HttpHeaders({
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Access-Control-Allow-Origin': '*',
        'Authorization': `Bearer ${accessToken}`
      }),
    };
    const data = {
      email: email,
      name: name,
      areaCode: areaCode,
      phoneNumber: phoneNumber,
      coolerId: coolerId,
      consents: consents
    };
    let response = await firstValueFrom(this.http.patch<APIDataResponse<User>>(url, data, httpOptions));
    return response;
  }

  async deleteUser(id: string): Promise<APINoDataResponse> {
    const accessToken = await this.accessToken(true);
    const url = `${this.apiUrl}/users/${id}`;
    const httpOptions = {
      headers: new HttpHeaders({
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Access-Control-Allow-Origin': '*',
        'Authorization': `Bearer ${accessToken}`
      }),
    };
    return firstValueFrom(this.http.delete<APINoDataResponse>(url, httpOptions));
  }

  async userExistsPhone(
    token: string,
    action: string,
    areaCode: string,
    number: string,
    ): Promise<APINoDataResponse> {
    const url = `${this.apiUrl}/users/exists/phone`;
    const httpOptions = {
      headers: new HttpHeaders({
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Access-Control-Allow-Origin': '*',
        'X-Recaptcha-Token': token,
        'X-Recaptcha-Action': action
      }),
      params: new HttpParams({
        fromObject: {
          areaCode: areaCode,
          number: number
        }
      })
    };
    return firstValueFrom(this.http.get<APINoDataResponse>(url, httpOptions));
  }

  async userExistEmail(
    token: string,
    action: string,
    email: string
  ) {
    const url = `${this.apiUrl}/users/exists/email`;
    const httpOptions = {
      headers: new HttpHeaders({
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Access-Control-Allow-Origin': '*',
        'X-Recaptcha-Token': token,
        'X-Recaptcha-Action': action
      }),
      params: new HttpParams({
        fromObject: {
          email: email
        }
      })
    };
    return firstValueFrom(this.http.get<APINoDataResponse>(url, httpOptions));
  }

  /**
   * Resolves whether a user exists by interpreting the response of an exists check
   * (`userExistEmail`/`userExistsPhone`): 200 → exists, 404 → free.
   * On 503 it retries `performCheck` (which should fetch a fresh reCAPTCHA token) up to
   * `retriesLeft` times; any other status is rethrown for the caller to handle.
   */
  async resolveUserExists(performCheck: () => Promise<unknown>, retriesLeft: number = 1): Promise<boolean> {
    try {
      await performCheck();
      return true;
    } catch (error) {
      if (error instanceof HttpErrorResponse) {
        if (error.status === 404) {
          return false;
        }
        if (error.status === 503 && retriesLeft > 0) {
          return this.resolveUserExists(performCheck, retriesLeft - 1);
        }
      }
      throw error;
    }
  }

  async addMercadopagoCard(
    token: any,
    issuer_id: any,
    payment_method_id: any,
    amount: any,
    installments: any,
    email: any,
    identificationType: any,
    identificationNumber: any,
    cardholderName: any,
    storeId: string | null = null,
    deviceId: string | null = null
  ): Promise<APINoDataResponse> {
    let accessToken = await this.accessToken();
    const url = `${this.apiUrl}/users/mercadopago/card`;
    const httpOptions = { headers: new HttpHeaders(this.authHeaders(accessToken)) };
    const data = {
      token: token,
      issuer_id: issuer_id,
      payment_method_id: payment_method_id,
      transaction_amount: Number(amount),
      installments: Number(installments),
      description: "Inscripción tarjeta de crédito.",
      payer: {
        email,
        identification: {
          type: identificationType,
          number: identificationNumber,
        },
      },
      clientId: this.clientId,
      cardholderName: cardholderName,
      storeId: storeId,
      deviceId: deviceId
    }
    return firstValueFrom(this.http.post<APINoDataResponse>(url, data, httpOptions));
  }

  async deleteMercadopagoCard(cardIndex: number = 0): Promise<APINoDataResponse> {
    let accessToken = await this.accessToken();
    const url = `${this.apiUrl}/users/mercadopago/card?clientId=${this.clientId}&cardIndex=${cardIndex}`;
    const httpOptions = {
      headers: new HttpHeaders({
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Access-Control-Allow-Origin': '*',
        'Authorization': `Bearer ${accessToken}`
      }),
    };
    return firstValueFrom(this.http.delete<APINoDataResponse>(url, httpOptions));
  }

  async getFaq(clientId: string | null = null): Promise<APIDataResponse<FAQ[]>> {
    const url = `${this.apiUrl}/faq`;
    let faqCache = this.sessionStorageService.getItem('faq');
    if (faqCache !== null) return this.emulateResponse(true, faqCache, 'Faq found in session storage');
    const paramsObject = {
      clientId: clientId,
    }
    return this.getFromApi<FAQ[]>(url, paramsObject, true, 'faq', 3600);
  }

  async usersHelp(contactFormData: ContactFormData): Promise<APINoDataResponse> {
    let accessToken = await this.accessToken();
    const url = `${this.apiUrl}/help`;
    const httpOptions = {
      headers: new HttpHeaders({
        'Accept': 'application/json',
        'Authorization': `Bearer ${accessToken}`
      }),
    };
    const formData: FormData = new FormData();
    formData.append('message', contactFormData.message);
    formData.append('category', contactFormData.category);
    formData.append('clientId', this.clientId);
    for (let image of contactFormData.images || []) {
      formData.append('images', image);
    }
    return firstValueFrom(this.http.post<APINoDataResponse>(url, formData, httpOptions));
  }

  async transactionsHelp(transactionId: string, contactFormData: ContactFormData, requiresToken: boolean = true): Promise<APINoDataResponse> {
    let accessToken: string | null = requiresToken ? await this.accessToken() : null;
    const url = `${this.apiUrl}/help/transactions/${transactionId}`;
    const headersObj: { [key: string]: string } = { 'Accept': 'application/json' };
    if (accessToken) { headersObj['Authorization'] = `Bearer ${accessToken}`; }
    const httpOptions = {
      headers: new HttpHeaders(headersObj),
    };
    const formData: FormData = new FormData();
    formData.append('message', contactFormData.message);
    formData.append('category', contactFormData.category);
    for (let image of contactFormData.images || []) {
      formData.append('images', image);
    }
    return firstValueFrom(this.http.post<APINoDataResponse>(url, formData, httpOptions));
  }

  anonHelp(contactFormData: ContactFormData): Promise<APINoDataResponse> {
    const url = `${this.apiUrl}/help/anon`;
    const httpOptions = {
      headers: new HttpHeaders({
        'Accept': 'application/json',
      }),
    };
    const formData: FormData = new FormData();
    formData.append('message', contactFormData.message);
    formData.append('category', contactFormData.category);
    formData.append('email', contactFormData.email);
    formData.append('clientId', this.clientId);
    for (let image of contactFormData.images || []) {
      formData.append('images', image);
    }
    return firstValueFrom(this.http.post<APINoDataResponse>(url, formData, httpOptions));
  }

  async getIssues(): Promise<APIDataResponse<UserIssue[]>> {
    const url = `${this.apiUrl}/help/me`;
    const accessToken = await this.accessToken();
    const httpOptions = {
      headers: new HttpHeaders({
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`
      }),
    };
    return firstValueFrom(this.http.get<APIDataResponse<UserIssue[]>>(url, httpOptions));
  }

  async getIssue(issueId: string): Promise<APIDataResponse<UserIssue>> {
    const url = `${this.apiUrl}/help/${issueId}`;
    const accessToken = await this.accessToken();
    const httpOptions = {
      headers: new HttpHeaders({
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`
      }),
      queryParams: new HttpParams().set('withImages', 'true')
    };
    return firstValueFrom(this.http.get<APIDataResponse<UserIssue>>(url, httpOptions));
  }

  async getIssueResponses(issueId: string): Promise<APIDataResponse<UserIssueResponse[]>> {
    const url = `${this.apiUrl}/help/${issueId}/responses`;
    const accessToken = await this.accessToken();
    const httpOptions = {
      headers: new HttpHeaders({
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`
      }),
    };
    return firstValueFrom(this.http.get<APIDataResponse<UserIssueResponse[]>>(url, httpOptions));
  }

  async postIssueResponse(issueId: string, message: string, files: File[] = []): Promise<APIDataResponse<UserIssueResponse>> {
    const url = `${this.apiUrl}/help/${issueId}/responses`;
    const accessToken = await this.accessToken();
    const httpOptions = {
      headers: new HttpHeaders({
        'Accept': 'application/json',
        'Authorization': `Bearer ${accessToken}`
      }),
    };
    const formData: FormData = new FormData();
    formData.append('message', message);
    for (let file of files) {
      formData.append('files', file);
    }
    return firstValueFrom(this.http.post<APIDataResponse<UserIssueResponse>>(url, formData, httpOptions));
  }

  async reopenIssue(issueId: string): Promise<APINoDataResponse> {
    const url = `${this.apiUrl}/help/${issueId}/reopen`;
    const accessToken = await this.accessToken();
    const httpOptions = {
      headers: new HttpHeaders({
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`
      }),
    };
    return firstValueFrom(this.http.post<APINoDataResponse>(url, {}, httpOptions));
  }

  async getSignedImageUrl(fileRoute: string, issueId: string): Promise<APIDataResponse<string>> {
    const url = `${this.apiUrl}/help/sign-issue-image`;
    const accessToken = await this.accessToken();
    const httpOptions = {
      headers: new HttpHeaders({
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`
      }),
    };
    const paramsObject = {
      fileRoute: fileRoute,
      issue_id: issueId
    }
    return firstValueFrom(this.http.get<APIDataResponse<string>>(url, { headers: httpOptions.headers, params: paramsObject }));
  }

  async addCoupon(couponId: string): Promise<APIDataResponse<{ coupon: Coupon }>> {
    let accessToken = await this.accessToken();
    const url = `${this.apiUrl}/users/coupons/${couponId}`;
    const httpOptions = {
      headers: new HttpHeaders({
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Access-Control-Allow-Origin': '*',
        'Authorization': `Bearer ${accessToken}`
      }),
    };
    let response = await firstValueFrom(this.http.post<APIDataResponse<{ coupon: Coupon }>>(url, {}, httpOptions));
    if (response.successful) {
      let coupons = this.sessionStorageService.getItem('coupons');
      if (coupons !== null) {
      coupons[couponId] = response.data.coupon;
        this.sessionStorageService.setItem('coupons', coupons);
      }
      else {
        this.sessionStorageService.setItem('coupons', { [couponId]: response.data.coupon });
      }
    }
    return response;
  }

  async getUsersCoupons(storeId: string | null = null): Promise<APIDataResponse<Coupons>> {
    let url = `${this.apiUrl}/users/coupons`;
    let sessionStorageKey = 'coupons';
    if (storeId !== null) {
      url += `?storeId=${storeId}`;
      sessionStorageKey = `coupons-${storeId}`;
    }
    return this.getFromApi<Coupons>(url, null, true, 'coupons');
  }

  async getClientPaymentMethods(clientId: string): Promise<APIDataResponse<{ [key: string]: boolean }>> {
    const url = `${this.apiUrl}/clients/${clientId}/payments`;
    return this.getFromApi<{ [key: string]: boolean }>(url);
  }

  async getLegalDocument(type: LegalDocumentType, lang: string): Promise<APIDataResponse<LegalDocument>> {
    const url = `${this.apiUrl}/legal-documents/${type}`;
    // Left on `getFromApi`'s default 300s session-storage TTL. Deliberately not lengthened:
    // consent is recorded against the version displayed, so a tab left open across a publish must
    // not keep showing — and let the user accept — a superseded text. `LegalDocumentsService`
    // additionally busts this by version when a caller names the one it expects.
    return this.getFromApi<LegalDocument>(url, { lang }, false, legalDocumentCacheKey(type, lang));
  }

  /** Drop the cached body for one document, so the next read comes from the API. */
  clearLegalDocumentCache(type: LegalDocumentType, lang: string): void {
    this.sessionStorageService.removeItem(legalDocumentCacheKey(type, lang));
  }

  /**
   * The current version of every legal document, without their bodies.
   *
   * Deliberately uncached — this is the value the acceptance gate compares against, so a stale
   * copy would mean gating on a version that is no longer current.
   */
  async getLegalDocumentRequirements(lang: string): Promise<APIDataResponse<LegalDocumentRequirement[]>> {
    const url = `${this.apiUrl}/legal-documents/requirements`;
    return this.getFromApi<LegalDocumentRequirement[]>(url, { lang }, false);
  }

  /** The signed-in user's consent state, including the documents they still owe. */
  async getConsents(): Promise<APIDataResponse<ConsentState>> {
    const url = `${this.apiUrl}/consents/me`;
    return this.getFromApi<ConsentState>(url);
  }

  /**
   * Record acceptance of one or more legal document versions.
   *
   * Each acceptance names the version actually rendered; the API rejects a stale one with
   * `409 CONSENT_STALE_VERSION` rather than recording consent to a text the user never saw.
   */
  async acceptConsentDocuments(
    acceptances: DocumentAcceptance[],
    channel: ConsentChannel
  ): Promise<APIDataResponse<ConsentState>> {
    const url = `${this.apiUrl}/consents/documents`;
    return this.postToApi<ConsentState>(url, { acceptances, channel });
  }

  /**
   * Grant or withdraw optional purposes. A partial map: absent keys are left untouched, and an
   * explicit `false` is a recorded refusal rather than a reset.
   */
  async setConsentPurposes(
    purposes: Partial<Record<ConsentPurpose, boolean>>,
    channel: ConsentChannel
  ): Promise<APIDataResponse<ConsentState>> {
    const url = `${this.apiUrl}/consents/purposes`;
    return this.putToApi<ConsentState>(url, { purposes, channel });
  }

  /**
   * The user's own consent history, newest first.
   *
   * The client half of the access right in Ley 21.719: the subject may ask what was recorded about
   * them and when. Reads the API's append-only event log, so the answer is the evidence itself
   * rather than a summary derived from current state.
   */
  async getConsentEvents(limit: number = 100): Promise<APIDataResponse<ConsentEvent[]>> {
    const url = `${this.apiUrl}/consents/me/events`;
    return this.getFromApi<ConsentEvent[]>(url, { limit: String(limit) });
  }

  async getLoginUrl(): Promise<APIDataResponse<LoginUrlResponseData>> {
    const redirect_uri = window.origin + '/callback';
    const url = `${this.apiUrl}/oauth2/generate-url?redirect_uri=${redirect_uri}`;
    const httpOptions = {
      headers: new HttpHeaders({
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      })
    };
    return firstValueFrom(this.http.get<APIDataResponse<LoginUrlResponseData>>(url, httpOptions));
  }

  async getCustomToken(token: string, codeVerifier: string): Promise<APIDataResponse<string>> {
    const url = `${this.apiUrl}/token/decode`;
    const httpOptions = {
      headers: new HttpHeaders({
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      }),
    };
    return firstValueFrom(this.http.post<APIDataResponse<string>>(url, { token: token, code_verifier: codeVerifier }, httpOptions));
  }

  async getVerificationEmail(email: string, isNewUser: boolean): Promise<APINoDataResponse> {
    const accessToken = await this.accessToken(true);
    const url = `${this.apiUrl}/users/verification-email?email=${email}&isNewUser=${isNewUser}`;
    const httpOptions = {
      headers: new HttpHeaders({
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`
      }),
    };
    return firstValueFrom(this.http.get<APINoDataResponse>(url, httpOptions));
  }

  async getBindEmail(email: string): Promise<APINoDataResponse> {
    const accessToken = await this.accessToken(true);
    const url = `${this.apiUrl}/users/bind-email?email=${email}`;
    const httpOptions = {
      headers: new HttpHeaders({
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`
      }),
    };
    return firstValueFrom(this.http.get<APINoDataResponse>(url, httpOptions));
  }

  async verifyEmail(token: string, isNewUser: boolean): Promise<APINoDataResponse> {
    const url = `${this.apiUrl}/users/verification-email-token`;
    const httpOptions = {
      headers: new HttpHeaders({
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      }),
    };
    return firstValueFrom(this.http.post<APINoDataResponse>(url, { token: token, isNewUser: isNewUser }, httpOptions));
  }

  async bindEmail(token: string): Promise<APIDataResponse<UpdateUserResponseData>> {
    const url = `${this.apiUrl}/users/bind-email-token`;
    const httpOptions = {
      headers: new HttpHeaders({
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      }),
    };
    let response = await firstValueFrom(this.http.post<APIDataResponse<UpdateUserResponseData>>(url, { token: token }, httpOptions));
    return response;
  }

  async sendIdentityDocumentQRURL(qrURL: string): Promise<APIDataResponse<User>> {
    const url = `${this.apiUrl}/users/identity-document/qr`;
    const accessToken = await this.accessToken();
    const httpOptions = {
      headers: new HttpHeaders({
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`
      })
    }
    let response = await firstValueFrom(this.http.post<APIDataResponse<User>>(url, { qrURL: qrURL }, httpOptions));
    return response;
  }

  async sendIdentityDocumentManualData(run: string, docNumber: string, birthDate: string, caducationDate: string): Promise<APIDataResponse<User>> {
    const url = `${this.apiUrl}/users/identity-document/manual`;
    const accessToken = await this.accessToken();
    const httpOptions = {
      headers: new HttpHeaders({
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`
      })
    }
    let response = await firstValueFrom(this.http.post<APIDataResponse<User>>(url, { run: run, docNumber: docNumber, birthDate: birthDate, caducationDate: caducationDate }, httpOptions));
    return response;
  }

  async getProductsGroupsDetails(clientId: string, productsGroupsIds: string[]): Promise<APIDataResponse<ProductsGroupsDetailsResponse>> {
    const url = `${this.apiUrl}/users/products-groups-details`;

    const sessionStorageKey = `products-groups-details-${clientId}-${productsGroupsIds.join('-')}`;
    const sessionStorageData = this.sessionStorageService.getItem(sessionStorageKey);
    if (sessionStorageData !== null) {
      return this.emulateResponse<ProductsGroupsDetailsResponse>(true, sessionStorageData, 'Data found in session storage');
    }

    const accessToken = await this.accessToken();
    if (!accessToken) {
      return this.emulateResponse(false, null, 'No auth token');
    }

    // Build HttpParams with clientId and repeated parameter names for array items
    let params = new HttpParams().set('clientId', clientId);
    productsGroupsIds.forEach(id => {
      params = params.append('productsGroupsIds', id);
    });

    const httpOptions = {
      headers: new HttpHeaders({
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Access-Control-Allow-Origin': '*',
        'Authorization': `Bearer ${accessToken}`
      }),
      params: params
    };

    try {
      const response: APIDataResponse<ProductsGroupsDetailsResponse> = await firstValueFrom(
        this.http.get<APIDataResponse<ProductsGroupsDetailsResponse>>(url, httpOptions)
      );

      if (response.successful) {
        this.sessionStorageService.setItem(sessionStorageKey, response.data, 3600);
      }

      return response;
    } catch (error: any) {
      // Return a proper error response structure
      return this.emulateResponse(false, null, error?.error?.message || error?.message || 'Error al cargar los detalles de los grupos de productos');
    }
  }
}
